import { isMultiPolygon } from './aoi';
import { downloadText } from './utils';

/**
 * Helpers for writing the vector results of an analysis as GeoJSON a GIS can open.
 * @module GEOJSON
 */

/**
 * @constant GEOJSON_MIME
 * @description Media type for GeoJSON, from RFC 7946. The generic `application/json` also opens in
 * QGIS, but only the specific type tells a browser, a file manager or a server what the file
 * actually is.
 * @memberof module:GEOJSON
 */
export const GEOJSON_MIME = 'application/geo+json';

/**
 * @constant LAYER_PROPERTY
 * @description Property naming which layer a feature belongs to. One file holds every layer of an
 * analysis - the area, the baseline, the transects, the coastlines - because a browser can only
 * hand over one file at a time and a GIS user would otherwise have to keep them paired by hand. In
 * QGIS the layers are separated again by filtering on this field, which is also what the style and
 * the layer panel key off.
 * @memberof module:GEOJSON
 */
export const LAYER_PROPERTY = 'layer';

/**
 * @function describe
 * @description The properties that make a feature say what it is when something else opens it.
 * `name` is what a GIS shows in its attribute table and offers as a label field. `title` and
 * `description` are the simplestyle convention, which the common web viewers - geojson.io among
 * them - read on their own: without them those viewers fall back to the geometry type, so a file
 * full of real measurements lists as "Polygon, LineString, MultiPolygon" and tells the reader
 * nothing. Both are written, because no single one of them is understood everywhere.
 * @param {String} name - What this feature is, in a few words.
 * @param {String} [description] - A longer line, when there is more worth saying.
 * @memberof module:GEOJSON
 * @returns {Object} Properties to merge into a feature.
 */
export const describe = (name, description) => ({
  name,
  title: name,
  ...(description ? { description } : {}),
});

/**
 * @function strokeStyle
 * @description simplestyle properties for a line, so a viewer draws it in the colour the app used
 * rather than in its own default.
 * @param {String} color - A `#rrggbb` colour.
 * @param {Object} [options]
 * @param {Number} [options.width=2] - Line width.
 * @param {Number} [options.opacity=1] - Line opacity.
 * @memberof module:GEOJSON
 * @returns {Object} Properties to merge into a feature.
 */
export const strokeStyle = (color, { width = 2, opacity = 1 } = {}) => ({
  stroke: color,
  'stroke-width': width,
  'stroke-opacity': opacity,
});

/**
 * @function fillStyle
 * @description simplestyle properties for an area, carrying the same colour and a matching outline.
 * @param {String} color - A `#rrggbb` colour.
 * @param {Number} [opacity=0.5] - Fill opacity.
 * @memberof module:GEOJSON
 * @returns {Object} Properties to merge into a feature.
 */
export const fillStyle = (color, opacity = 0.5) => ({
  fill: color,
  'fill-opacity': opacity,
  stroke: color,
  'stroke-width': 1,
  'stroke-opacity': 0.8,
});

/**
 * @function isCoordinate
 * @description Whether a pair is a usable `[longitude, latitude]`. Coordinates reach here from
 * drawn overlays, parsed KML and Earth Engine alike, and one malformed pair makes the whole file
 * unreadable rather than just its own feature.
 * @param {*} pair - Candidate coordinate.
 * @memberof module:GEOJSON
 * @returns {Boolean} True when the pair is a finite lon/lat inside the valid range.
 */
export const isCoordinate = (pair) =>
  Array.isArray(pair) &&
  pair.length >= 2 &&
  Number.isFinite(Number(pair[0])) &&
  Number.isFinite(Number(pair[1])) &&
  Math.abs(Number(pair[1])) <= 90 &&
  Math.abs(Number(pair[0])) <= 180;

/**
 * @function toCoordinate
 * @description Narrows a coordinate to the two numbers GeoJSON wants, dropping any third value.
 * KML carries an altitude that none of these analyses use, and QGIS reads the layer as 3D when it
 * is present.
 * @param {Array<Number>} pair - `[longitude, latitude]`, possibly with altitude.
 * @memberof module:GEOJSON
 * @returns {Array<Number>} `[longitude, latitude]`.
 */
export const toCoordinate = (pair) => [Number(pair[0]), Number(pair[1])];

/**
 * @function normalizeProperties
 * @description Flattens a feature's properties to what a GIS attribute table can hold: numbers,
 * strings, booleans and nulls.
 * A GIS derives its columns from the features it reads, so a value it cannot type - an object, an
 * array, a Date - either becomes an unreadable column or drops the field. Anything structured is
 * written as JSON text instead, which at least survives the trip intact, and `undefined` becomes
 * an explicit null so every feature of a layer carries the same set of columns.
 * @param {Object} [properties] - Raw properties.
 * @memberof module:GEOJSON
 * @returns {Object} Properties safe to write.
 */
export const normalizeProperties = (properties = {}) =>
  Object.entries(properties).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) acc[key] = null;
    else if (value instanceof Date) acc[key] = value.toISOString();
    else if (typeof value === 'object') acc[key] = JSON.stringify(value);
    else if (typeof value === 'number') acc[key] = Number.isFinite(value) ? value : null;
    else acc[key] = value;
    return acc;
  }, {});

/**
 * @function hasValidPositions
 * @description Whether every position nested anywhere inside a geometry's coordinates is a usable
 * longitude/latitude pair.
 * @param {*} coordinates - A position, or any depth of nested arrays of them.
 * @memberof module:GEOJSON
 * @returns {Boolean} True when they are all valid and there is at least one.
 */
const hasValidPositions = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return false;
  if (typeof coordinates[0] === 'number') return isCoordinate(coordinates);
  return coordinates.every(hasValidPositions);
};

/**
 * @function isWritableGeometry
 * @description Whether a geometry can be written as it stands.
 * Geometry that an analysis hands over whole is not built by the helpers here and so has never
 * been looked at: a layer still held in a projected CRS carries eastings and northings in the
 * hundreds of thousands, which as longitude and latitude are meaningless. Written out anyway they
 * produce a file that opens on the far side of the world, or that a GIS refuses entirely - and
 * either way the mistake surfaces far from where it was made. Checking here turns that into an
 * export that says it has nothing to write.
 * @param {Object} [geometry] - A GeoJSON geometry.
 * @memberof module:GEOJSON
 * @returns {Boolean} True when every position in it is valid.
 */
export const isWritableGeometry = (geometry) => {
  if (!geometry?.type) return false;
  if (geometry.type === 'GeometryCollection') {
    return (geometry.geometries || []).length > 0 && geometry.geometries.every(isWritableGeometry);
  }
  return hasValidPositions(geometry.coordinates);
};

/**
 * @function feature
 * @description Builds one GeoJSON feature.
 * @param {Object} geometry - A GeoJSON geometry.
 * @param {String} layer - Which layer it belongs to, written as {@link LAYER_PROPERTY}.
 * @param {Object} [properties] - Attributes to carry alongside it.
 * @memberof module:GEOJSON
 * @returns {Object|null} The feature, or `null` when there is no geometry to write.
 */
export const feature = (geometry, layer, properties = {}) => {
  if (!isWritableGeometry(geometry)) return null;
  return {
    type: 'Feature',
    geometry,
    properties: normalizeProperties({ [LAYER_PROPERTY]: layer, ...properties }),
  };
};

/**
 * @function pointFeature
 * @description A single point.
 * @param {Array<Number>} coordinate - `[longitude, latitude]`.
 * @param {String} layer - Layer name.
 * @param {Object} [properties] - Attributes.
 * @memberof module:GEOJSON
 * @returns {Object|null} The feature, or `null` when the coordinate is unusable.
 */
export const pointFeature = (coordinate, layer, properties) => {
  if (!isCoordinate(coordinate)) return null;
  return feature({ type: 'Point', coordinates: toCoordinate(coordinate) }, layer, properties);
};

/**
 * @function lineFeature
 * @description A line through the coordinates given, dropping any that are unusable.
 * @param {Array<Array<Number>>} coordinates - `[longitude, latitude]` pairs.
 * @param {String} layer - Layer name.
 * @param {Object} [properties] - Attributes.
 * @memberof module:GEOJSON
 * @returns {Object|null} The feature, or `null` when fewer than two coordinates survive.
 */
export const lineFeature = (coordinates, layer, properties) => {
  const clean = (coordinates || []).filter(isCoordinate).map(toCoordinate);
  if (clean.length < 2) return null;
  return feature({ type: 'LineString', coordinates: clean }, layer, properties);
};

/**
 * @function closedRing
 * @description A ring with its unusable positions dropped and closed, as GeoJSON wants it.
 * @param {Array<Array<Number>>} ring - `[longitude, latitude]` pairs, open or closed.
 * @memberof module:GEOJSON
 * @returns {Array<Array<Number>>|null} `null` when fewer than three positions survive.
 */
const closedRing = (ring) => {
  const clean = (ring || []).filter(isCoordinate).map(toCoordinate);
  if (clean.length < 3) return null;
  const [first] = clean;
  const last = clean[clean.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? clean : [...clean, first];
};

/**
 * @function polygonFeature
 * @description A polygon from a ring. The ring is closed here if it is not already: the app keeps
 * rings open, because Google Maps closes its own polygons, while GeoJSON requires the first and
 * last position to match. An area read from a KML of several polygons, or of one with holes, is
 * written as a MultiPolygon.
 * @param {Array} ring - `[longitude, latitude]` pairs, or MultiPolygon coordinates.
 * @param {String} layer - Layer name.
 * @param {Object} [properties] - Attributes.
 * @memberof module:GEOJSON
 * @returns {Object|null} The feature, or `null` when fewer than three coordinates survive.
 */
export const polygonFeature = (ring, layer, properties) => {
  if (isMultiPolygon(ring)) {
    const polygons = ring
      .map(([outer, ...holes]) => [closedRing(outer), ...holes.map(closedRing).filter(Boolean)])
      .filter(([outer]) => outer);
    return polygons.length ? feature({ type: 'MultiPolygon', coordinates: polygons }, layer, properties) : null;
  }
  const closed = closedRing(ring);
  return closed ? feature({ type: 'Polygon', coordinates: [closed] }, layer, properties) : null;
};

/**
 * @function adoptFeature
 * @description Re-labels a feature that is already GeoJSON - one evaluated from Earth Engine, or
 * one an analysis stored as it came - so it joins the file under a layer of its own, keeping its
 * geometry untouched and its own attributes alongside.
 * @param {Object} source - An existing GeoJSON feature.
 * @param {String} layer - Layer name.
 * @param {Object} [properties] - Extra attributes to merge in.
 * @memberof module:GEOJSON
 * @returns {Object|null} The relabelled feature, or `null` when it carries no geometry.
 */
export const adoptFeature = (source, layer, properties = {}) => {
  if (!source?.geometry) return null;
  return feature(source.geometry, layer, { ...(source.properties || {}), ...properties });
};

/**
 * @function adoptFeatures
 * @description Takes GeoJSON in whatever shape an analysis happens to hold it - a collection, a
 * single feature, a bare geometry, or a list of any of those - and returns features labelled for
 * one layer.
 * The modules do not agree on this: some keep what Earth Engine evaluated, some keep what turf
 * produced, some keep a geometry on its own. Sorting it out here means each module's export can
 * say what it wants written without first having to establish what it is holding.
 * @param {*} source - Any of the shapes above.
 * @param {String} layer - Layer name.
 * @param {Object} [properties] - Attributes to merge into every feature.
 * @memberof module:GEOJSON
 * @returns {Array<Object>} The features, empty when there is no geometry in there.
 */
export const adoptFeatures = (source, layer, properties = {}) => {
  if (!source) return [];

  if (Array.isArray(source)) return source.flatMap((entry) => adoptFeatures(entry, layer, properties));

  if (source.type === 'FeatureCollection') {
    return (source.features || []).flatMap((entry, index) =>
      adoptFeatures(entry, layer, { ...properties, feature_index: index }),
    );
  }

  if (source.type === 'Feature') {
    const adopted = adoptFeature(source, layer, properties);
    return adopted ? [adopted] : [];
  }

  if (source.type && source.coordinates) {
    const built = feature(source, layer, properties);
    return built ? [built] : [];
  }

  /**
   * A plain object carrying a `geometry` - what an analysis produces when it keeps a geometry
   * alongside its own fields, such as `{id, year, geometry}`. Without this it matched none of the
   * shapes above and was dropped in silence, which is how a whole layer went missing from a file
   * that otherwise looked complete.
   */
  if (source.geometry) {
    const { geometry, properties: own, ...rest } = source;
    const built = adoptFeature(source, layer, { ...rest, ...properties });
    return built ? [built] : [];
  }

  return [];
};

/**
 * @function featureCollection
 * @description Gathers features into a collection, dropping the ones that could not be built.
 * @param {Array<Object|null>} features - Features, as returned by the builders above.
 * @memberof module:GEOJSON
 * @returns {Object} An RFC 7946 FeatureCollection, in WGS84 as the specification requires.
 */
export const featureCollection = (features) => ({
  type: 'FeatureCollection',
  features: (features || []).flat().filter(Boolean),
});

/**
 * @function geoJsonFileName
 * @description Names an export after its module and the day it was taken, so several exports can
 * live in the same folder without overwriting one another.
 * @param {String} module - Module key, e.g. `'shoreline'`.
 * @memberof module:GEOJSON
 * @returns {String} A file name ending in `.geojson`.
 */
export const geoJsonFileName = (module) => `cassie-${module}-${new Date().toISOString().slice(0, 10)}.geojson`;

/**
 * @function downloadGeoJSON
 * @description Hands a collection to the browser as a `.geojson` file.
 * @param {Object} collection - A FeatureCollection.
 * @param {String} module - Module key, used to name the file.
 * @memberof module:GEOJSON
 * @returns {Boolean} False when there was nothing to write, so the caller can say so.
 */
export const downloadGeoJSON = (collection, module) => {
  if (!collection?.features?.length) return false;
  downloadText(JSON.stringify(collection), geoJsonFileName(module), GEOJSON_MIME);
  return true;
};
